<!--
  The one practice-forms renderer - tasks/patient-forms.md section 5 / 13.1.

  There is NOT ONE *ngIf IN THIS TEMPLATE THAT DECIDES VISIBILITY. `renderedSections` already holds
  only the questions `visibleQuestions(schema, answers)` returned, and the errors already come from
  `validateAnswers`. A `*ngIf="answers['q1'] === 'yes'"` here would be a second implementation of
  `showIf` - the one the patient actually sees, and the one the server would then disagree with.

  Patient-facing and mobile-first: this is filled in on a phone, in a waiting room, one-handed.
  Every control has a real label, help text is wired with aria-describedby, option rows are a
  44px touch target, and nothing depends on hover.
-->

<div class="dfr" [class.dfr-readonly]="readonly">

  <p class="dfr-empty" *ngIf="!hasContent">There are no questions to show.</p>

  <!-- ────────────────────────────────────────────────────────────────────────────────────────
       READ-ONLY SUMMARY - the patient-file panel's whole use of this component.
       Answers as a person reads them: option text, not option value; Yes/No, not true/false.
       ──────────────────────────────────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="readonly">
    <section class="dfr-section" *ngFor="let rendered of renderedSections; trackBy: trackBySection">
      <h2 class="dfr-section-title">{{ rendered.section.title }}</h2>
      <p class="dfr-section-description" *ngIf="rendered.section.description">{{ rendered.section.description }}</p>

      <dl class="dfr-summary">
        <ng-container *ngFor="let question of rendered.questions; trackBy: trackByQuestion">

          <div class="dfr-static" *ngIf="question.type === 'statictext'">
            <p>{{ question.question }}</p>
          </div>

          <div class="dfr-summary-row" *ngIf="question.type !== 'statictext'"
               [attr.data-question-id]="question.id" [attr.data-question-type]="question.type">
            <dt class="dfr-summary-label">{{ question.question }}</dt>
            <dd class="dfr-summary-value">
              <span class="dfr-no-answer" *ngIf="!hasAnswer(question)">Not answered</span>
              <ng-container *ngIf="hasAnswer(question)">
                <img class="dfr-signature-image" *ngIf="question.type === 'signature'"
                     [src]="answerOf(question)" alt="Signature provided by the patient" />
                <span class="dfr-answer-text" *ngIf="question.type !== 'signature'">{{ displayAnswer(question) }}</span>
              </ng-container>
            </dd>
          </div>

        </ng-container>
      </dl>
    </section>
  </ng-container>

  <!-- ────────────────────────────────────────────────────────────────────────────────────────
       EDITABLE FORM
       ──────────────────────────────────────────────────────────────────────────────────────── -->
  <form class="dfr-form" *ngIf="!readonly" [formGroup]="form" novalidate (ngSubmit)="$event.preventDefault()">

    <section class="dfr-section" *ngFor="let rendered of renderedSections; trackBy: trackBySection">
      <h2 class="dfr-section-title">{{ rendered.section.title }}</h2>
      <p class="dfr-section-description" *ngIf="rendered.section.description">{{ rendered.section.description }}</p>

      <div class="dfr-question"
           *ngFor="let question of rendered.questions; trackBy: trackByQuestion"
           [attr.data-question-id]="question.id"
           [attr.data-question-type]="question.type"
           [ngSwitch]="question.type">

        <!-- ── statictext: body copy. Carries no control and never appears in answers. ── -->
        <div class="dfr-static" *ngSwitchCase="'statictext'">
          <p>{{ question.question }}</p>
        </div>

        <!-- ── text ── -->
        <mat-form-field appearance="outline" class="dfr-field" *ngSwitchCase="'text'">
          <mat-label>{{ question.question }}<span class="dfr-required" *ngIf="question.required"
              aria-hidden="true">&nbsp;*</span></mat-label>
          <input matInput type="text"
                 [formControlName]="question.id"
                 [attr.aria-required]="question.required ? 'true' : null"
                 [placeholder]="question.placeholder || ''"
                 [errorStateMatcher]="matcherFor(question)" />
          <mat-hint *ngIf="question.helpText">{{ question.helpText }}</mat-hint>
          <mat-error class="dfr-error">{{ errorFor(question) }}</mat-error>
        </mat-form-field>

        <!-- ── textarea ── -->
        <mat-form-field appearance="outline" class="dfr-field" *ngSwitchCase="'textarea'">
          <mat-label>{{ question.question }}<span class="dfr-required" *ngIf="question.required"
              aria-hidden="true">&nbsp;*</span></mat-label>
          <textarea matInput cdkTextareaAutosize cdkAutosizeMinRows="3" cdkAutosizeMaxRows="12"
                    [formControlName]="question.id"
                    [attr.aria-required]="question.required ? 'true' : null"
                    [placeholder]="question.placeholder || ''"
                    [errorStateMatcher]="matcherFor(question)"></textarea>
          <mat-hint *ngIf="question.helpText">{{ question.helpText }}</mat-hint>
          <mat-error class="dfr-error">{{ errorFor(question) }}</mat-error>
        </mat-form-field>

        <!--
          ── number ──
          min/max are bound as ATTRIBUTES, not as [min]/[max]. The bracketed form activates
          Angular's own MinValidator/MaxValidator, which would put a second validity opinion on the
          control; `validateAnswers` is the only one allowed to have one here.
        -->
        <mat-form-field appearance="outline" class="dfr-field" *ngSwitchCase="'number'">
          <mat-label>{{ question.question }}<span class="dfr-required" *ngIf="question.required"
              aria-hidden="true">&nbsp;*</span></mat-label>
          <input matInput type="number" inputmode="decimal"
                 [formControlName]="question.id"
                 [attr.aria-required]="question.required ? 'true' : null"
                 [placeholder]="question.placeholder || ''"
                 [attr.min]="question.min"
                 [attr.max]="question.max"
                 [errorStateMatcher]="matcherFor(question)" />
          <mat-hint *ngIf="question.helpText">{{ question.helpText }}</mat-hint>
          <mat-error class="dfr-error">{{ errorFor(question) }}</mat-error>
        </mat-form-field>

        <!--
          ── date ──
          A NATIVE date input, not mat-datepicker. Answers must be an ISO `YYYY-MM-DD` string
          (13.5, "date validity"), which is exactly what `input[type=date]` produces and consumes;
          a datepicker hands back a Date/moment that would have to be reformatted on the way in and
          out, and on a phone the native picker is the better control anyway.
        -->
        <mat-form-field appearance="outline" class="dfr-field" *ngSwitchCase="'date'">
          <mat-label>{{ question.question }}<span class="dfr-required" *ngIf="question.required"
              aria-hidden="true">&nbsp;*</span></mat-label>
          <input matInput type="date"
                 [formControlName]="question.id"
                 [attr.aria-required]="question.required ? 'true' : null"
                 [errorStateMatcher]="matcherFor(question)" />
          <mat-hint *ngIf="question.helpText">{{ question.helpText }}</mat-hint>
          <mat-error class="dfr-error">{{ errorFor(question) }}</mat-error>
        </mat-form-field>

        <!-- ── select ── -->
        <mat-form-field appearance="outline" class="dfr-field" *ngSwitchCase="'select'">
          <mat-label>{{ question.question }}<span class="dfr-required" *ngIf="question.required"
              aria-hidden="true">&nbsp;*</span></mat-label>
          <mat-select [formControlName]="question.id"
                      [attr.aria-required]="question.required ? 'true' : null"
                      [errorStateMatcher]="matcherFor(question)">
            <mat-option *ngFor="let option of options(question)" [value]="option.value">{{ option.text }}</mat-option>
          </mat-select>
          <mat-hint *ngIf="question.helpText">{{ question.helpText }}</mat-hint>
          <mat-error class="dfr-error">{{ errorFor(question) }}</mat-error>
        </mat-form-field>

        <!-- ── radio ── -->
        <fieldset class="dfr-fieldset" *ngSwitchCase="'radio'">
          <legend class="dfr-legend">
            {{ question.question }}<span class="dfr-required" *ngIf="question.required" aria-hidden="true">*</span>
          </legend>
          <p class="dfr-help" [id]="helpId(question)" *ngIf="question.helpText">{{ question.helpText }}</p>
          <mat-radio-group class="dfr-options"
                           [formControlName]="question.id"
                           [attr.aria-describedby]="question.helpText ? helpId(question) : null"
                           [attr.aria-required]="question.required ? 'true' : null">
            <mat-radio-button class="dfr-option" *ngFor="let option of options(question)" [value]="option.value">
              {{ option.text }}
            </mat-radio-button>
          </mat-radio-group>
          <p class="dfr-error" role="alert" *ngIf="shouldShowError(question)">{{ errorFor(question) }}</p>
        </fieldset>

        <!--
          ── checkbox (multi) ──
          ONE control holding `string[]`, not one control per option: 13.1 says a checkbox answer
          IS an array, and `evaluateShowIf` tests membership in it.
        -->
        <fieldset class="dfr-fieldset" *ngSwitchCase="'checkbox'">
          <legend class="dfr-legend">
            {{ question.question }}<span class="dfr-required" *ngIf="question.required" aria-hidden="true">*</span>
          </legend>
          <p class="dfr-help" [id]="helpId(question)" *ngIf="question.helpText">{{ question.helpText }}</p>
          <div class="dfr-options" role="group"
               [attr.aria-label]="question.question"
               [attr.aria-describedby]="question.helpText ? helpId(question) : null">
            <mat-checkbox class="dfr-option" *ngFor="let option of options(question)"
                          [checked]="isChecked(question, option)"
                          (change)="toggleOption(question, option, $event.checked)">
              {{ option.text }}
            </mat-checkbox>
          </div>
          <p class="dfr-error" role="alert" *ngIf="shouldShowError(question)">{{ errorFor(question) }}</p>
        </fieldset>

        <!--
          ── yesno ──
          Two large targets rather than a slide toggle: a toggle has no neutral state, so an
          untouched one reads as a deliberate "No" the patient never gave.
        -->
        <fieldset class="dfr-fieldset" *ngSwitchCase="'yesno'">
          <legend class="dfr-legend">
            {{ question.question }}<span class="dfr-required" *ngIf="question.required" aria-hidden="true">*</span>
          </legend>
          <p class="dfr-help" [id]="helpId(question)" *ngIf="question.helpText">{{ question.helpText }}</p>
          <mat-button-toggle-group class="dfr-yesno"
                                   [formControlName]="question.id"
                                   [attr.aria-describedby]="question.helpText ? helpId(question) : null">
            <mat-button-toggle [value]="true">Yes</mat-button-toggle>
            <mat-button-toggle [value]="false">No</mat-button-toggle>
          </mat-button-toggle-group>
          <p class="dfr-error" role="alert" *ngIf="shouldShowError(question)">{{ errorFor(question) }}</p>
        </fieldset>

        <!--
          ── scale ──
          Discrete points, not a slider. A slider thumb is unusable one-handed on a phone and gives
          no way to see which value is selected without looking closely; a numbered row of targets
          is what every printed pain scale already is.
        -->
        <fieldset class="dfr-fieldset" *ngSwitchCase="'scale'">
          <legend class="dfr-legend">
            {{ question.question }}<span class="dfr-required" *ngIf="question.required" aria-hidden="true">*</span>
          </legend>
          <p class="dfr-help" [id]="helpId(question)" *ngIf="question.helpText">{{ question.helpText }}</p>
          <mat-button-toggle-group class="dfr-scale"
                                   [formControlName]="question.id"
                                   [attr.aria-describedby]="question.helpText ? helpId(question) : null">
            <mat-button-toggle *ngFor="let point of scalePoints(question)" [value]="point">{{ point }}</mat-button-toggle>
          </mat-button-toggle-group>
          <p class="dfr-error" role="alert" *ngIf="shouldShowError(question)">{{ errorFor(question) }}</p>
        </fieldset>

        <!--
          ── signature ──
          The EXISTING SignaturePadComponent, the same one the invite wizard signs consent with.
          `format="base64"` is what makes the answer the data URL 13.1 specifies.
        -->
        <div class="dfr-signature" *ngSwitchCase="'signature'">
          <p class="dfr-question-text" [id]="labelId(question)">
            {{ question.question }}<span class="dfr-required" *ngIf="question.required" aria-hidden="true">*</span>
          </p>
          <p class="dfr-help" [id]="helpId(question)" *ngIf="question.helpText">{{ question.helpText }}</p>
          <div class="dfr-signature-pad"
               [attr.aria-labelledby]="labelId(question)"
               [attr.aria-describedby]="question.helpText ? helpId(question) : null">
            <app-signature-pad [emitOnDragEnd]="true" format="base64" [hideDetails]="true"
                               (done)="onSignature(question, $event)"
                               (cleared)="onSignature(question, '')">
            </app-signature-pad>
          </div>
          <p class="dfr-error" role="alert" *ngIf="shouldShowError(question)">{{ errorFor(question) }}</p>
        </div>

        <!-- An unknown type is a schema `validateSchema` rejects at publish time. Say so plainly
             rather than rendering a blank space the patient cannot answer. -->
        <p class="dfr-unsupported" *ngSwitchDefault>
          This question cannot be displayed. Please contact the practice.
        </p>

      </div>
    </section>
  </form>

</div>
